import { createInstance } from "i18next"
import { type MessageKey, en } from "@/i18n/en"
import type { useT } from "./i18n"
import type { Schemas } from "./api"

export type Finding = Schemas["DataQualityFinding"]
export type FindingsPage = Schemas["DataQualityFindingsPage"]
export type FindingStatus = Schemas["DataQualityFindingStatus"]
export type FindingSeverity = Schemas["DataQualitySeverity"]
export type FindingClass = Finding["classification"]

export const STATUSES: readonly FindingStatus[] = ["open", "acknowledged", "resolved", "superseded"]
export const SEVERITIES: readonly FindingSeverity[] = ["critical", "error", "warning", "info"]

export const CLASSIFICATION_COPY: Record<FindingClass, { label: string; hint: string; labelKey: MessageKey; hintKey: MessageKey; tone: "primary" | "outline" | "neutral" | "warning" }> = {
  integrity: { label: en["findings.classification.integrity"], hint: en["findings.classification.integrityHint"], labelKey: "findings.classification.integrity", hintKey: "findings.classification.integrityHint", tone: "primary" },
  quality: { label: en["findings.classification.quality"], hint: en["findings.classification.qualityHint"], labelKey: "findings.classification.quality", hintKey: "findings.classification.qualityHint", tone: "outline" },
  builtin: { label: en["findings.classification.builtin"], hint: en["findings.classification.builtinHint"], labelKey: "findings.classification.builtin", hintKey: "findings.classification.builtinHint", tone: "neutral" },
  unverified: { label: en["findings.classification.unverified"], hint: en["findings.classification.unverifiedHint"], labelKey: "findings.classification.unverified", hintKey: "findings.classification.unverifiedHint", tone: "warning" },
}

// Plain-module callers use English; components pass their current translator.
const english = createInstance()
void english.init({ lng: "en", resources: { en: { translation: en } }, keySeparator: false, interpolation: { escapeValue: false } })
const defaultT = english.getFixedT("en")

/** The rule as one readable label: `commerce.SalesOrder · total_non_negative` or `built-in · temporal`. */
export function ruleLabel(finding: Finding, t: ReturnType<typeof useT> = defaultT): string {
  const rule = finding.ruleRef
  return rule.kind === "builtin" ? t("findings.ruleBuiltIn", { id: rule.id }) : `${rule.owner.name} · ${rule.invariantId}`
}

/** A pinned rule version for display: content hashes shortened, the legacy sentinel spelled out. */
export function ruleVersionLabel(version: string, t: ReturnType<typeof useT> = defaultT): string {
  if (version === "legacy-unpinned/v1") return t("findings.unpinnedVersion")
  if (version.startsWith("sha256:")) return `sha256:${version.slice(7, 19)}`
  return version
}

/** One sentence for the typed outcome. */
export function describeOutcome(outcome: Finding["outcome"], t: ReturnType<typeof useT> = defaultT): string {
  switch (outcome.kind) {
    case "not_satisfied":
      return t("findings.outcome.notSatisfied")
    case "non_boolean":
      return t("findings.outcome.nonBoolean", { actual: outcome.actual })
    case "evaluation_failed":
      return t("findings.outcome.evaluationFailed", { reason: outcome.reason })
    case "observed_out_of_band": {
      const key = outcome.reason === "no_declared_path"
        ? outcome.from ? "findings.outcome.noPathFrom" : "findings.outcome.noPath"
        : outcome.from ? "findings.outcome.multiplePathsFrom" : "findings.outcome.multiplePaths"
      return t(key, { property: outcome.property, state: outcome.state, from: outcome.from ?? "" })
    }
    case "invalid_temporal_observation":
      return t("findings.outcome.invalidTemporal", { property: outcome.property, value: outcome.value, reason: outcome.reason })
    case "identity_projection_isolation":
      return t("findings.outcome.identityIsolation", { code: outcome.code, detail: outcome.detail })
  }
}
